<template>
  <v-row align="center" justify="center">
    <v-col cols="12">
      <v-card elevation="0" tile>
        <v-card-title class="grey lighten-4">
          {{ $t("hr.menu.expense_approval") }}
          <v-spacer />
          <id-button @click="onRefreshData">
            <template #icon>
              <v-icon left>mdi-refresh</v-icon>
            </template>
            {{ $t("buttons.refresh") }}
          </id-button>
        </v-card-title>
        <v-data-table
          :headers="table.headers"
          :items="table.data"
          :options.sync="table.options"
          :server-items-length="table.totalItems"
          single-select
          class="elevation-1"
          :loading="table.loading"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        >
          <template slot="item" slot-scope="row">
            <tr :key="row.item.id">
              <td class="text-xs-left no-wrap">
                <employee-profile :employee="row.item.expense.employee" />
              </td>
              <td class="text-xs-left no-wrap">
                {{ row.item.expense.name || "-" }}
              </td>
              <td class="text-xs-left no-wrap">
                {{ $helpers.getConvertCurrency(row.item.expense.requested) }}
              </td>
              <td>
                <template v-if="row.item.expense.travel">
                  <p class="ma-0 caption">
                    <v-icon size="20" color="teal">mdi-account</v-icon>
                    {{ row.item.expense.travel.employee.firstName }}
                    {{ row.item.expense.travel.employee.lastName }}
                  </p>
                  <p class="ma-0 caption">
                    <v-icon size="20" color="teal">mdi-map-marker</v-icon>{{ row.item.expense.travel.address }}
                  </p>
                  <p class="ma-0 caption">
                    <v-icon size="20" color="teal">mdi-calendar-range</v-icon>
                    {{ row.item.expense.travel.beginDate | formattedDateTimeMinute }} -
                    {{ row.item.expense.travel.endDate | formattedDateTimeMinute }}
                  </p>
                </template>
                <v-icon v-else color="red">mdi-close</v-icon>
              </td>
              <td>
                <template v-if="row.item.expense.expenseManagement">
                  <p class="ma-0">
                    {{ row.item.expense.expenseManagement.name }}
                  </p>
                  <p v-if="row.item.expense.expenseManagement.project" class="ma-0 caption">
                    <v-avatar size="14" :color="row.item.expense.expenseManagement.project.color" />
                    {{ row.item.expense.expenseManagement.project.name }}
                  </p>
                </template>
                <v-icon v-else color="red">mdi-close</v-icon>
              </td>
              <td class="text-xs-left no-wrap">
                <request-status-label :status="row.item.expense.requestStatus" />
              </td>
              <td class="text-xs-left no-wrap">
                {{ row.item.expense.timestamp | formattedDate }}
              </td>
              <td class="no-wrap">
                <file-download-button :url="row.item.expense.attachment" />
              </td>
              <td class="text-right">
                <v-btn small depressed color="primary" dark @click.stop="onClickExpenseApproval(row.item)">
                  {{ $t("buttons.action") }}
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <expense-approval-dialog
      v-model="dialog.status"
      :data.sync="dialog.data"
      @close="onCloseDialog"
      @success="onSuccessApproval"
    />
  </v-row>
</template>

<script>
  import * as GQL from "./query";

  export default {
    name: "ExpenseApproval",
    components: {
      RequestStatusLabel: () => import("@/components/global/request-status-label"),
      "expense-approval-dialog": () => import("./components/confirm-dialog")
    },
    data: vm => ({
      dialog: {
        status: false,
        data: null
      },
      table: {
        loading: false,
        data: [],
        criteria: {
          employees: [],
          beginDate: null,
          endDate: null
        },
        options: {},
        pagination: {
          rowsPerPage: 50,
          sortBy: "expense.requestDateTime",
          descending: false
        },
        totalItems: 0,
        totalPages: 0,
        selected: [],
        headers: [
          {
            text: vm.$t("hr.expense_approval.employee"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.expense_approval.description"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.expense_approval.amount"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.expense.travel"),
            value: "",
            align: "left",
            sortable: false,
            class: ["primary--text"]
          },
          {
            text: vm.$t("global.expense_management"),
            value: "",
            align: "left",
            sortable: false,
            class: ["primary--text"]
          },
          {
            text: vm.$t("hr.expense_approval.status"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.expense_approval.request_date"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.expense_approval.document"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },

          {
            text: "",
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-right"]
          }
        ]
      }
    }),
    watch: {
      "table.options": {
        handler(options) {
          let { itemsPerPage, page } = options;
          this.table.pagination.rowsPerPage = itemsPerPage;
          this.table.pagination.page = page;
          this.fetchItems();
        },
        deep: true
      }
    },
    methods: {
      onRefreshData() {
        this.fetchItems();
      },
      async fetchItems() {
        this.table.loading = true;
        await this.$apollo
          .query({
            query: GQL.MANAGER_APPROVER_EXPENSE,
            variables: {
              id: this.$store.state.auth.user.id
            },
            fetchPolicy: "no-cache",
            errorPolicy: "all"
          })
          .then(({ data, errors }) => {
            if (!errors && !data.error) {
              this.table.data = data.employee.pendingApprovalExpenseRequests || [];
              this.table.totalItems = data.employee.pendingApprovalExpenseRequests.length || 0;
              this.table.totalPages = data.employee.pendingApprovalExpenseRequests.length || 0;
            }
          })
          .catch(e => {
            this.$helpers.showNotification(e.message);
          })
          .finally(() => {
            this.table.loading = false;
          });
      },
      onClickExpenseApproval(expense) {
        this.dialog.data = expense;
        this.dialog.status = true;
      },
      onCloseDialog() {
        this.dialog.data = null;
        this.dialog.status = false;
      },
      onSuccessApproval() {
        this.onCloseDialog();
        this.onRefreshData();
      }
    }
  };
</script>

<style scoped></style>
